import {DateTime} from "luxon";
import {container, inject, injectable, registry} from "tsyringe";
import {transformer} from "../decorators/transformer.decorator";
import {ProjectDTO} from "../domain/dtos/project.dto";
import {Employee} from "../domain/entities/employee.entity";
import {Project} from "../domain/entities/project.entity";
import {EmployeeRepository} from "../domain/repositories/employee.repository";
import {ProjectTypeRepository} from "../domain/repositories/project-type.repository";
import {TransformerHelper} from "../helpers/transformer.helper";
import {AbstractDtoToEntityTransformer} from "./abstract-dto-to-entity.transformer";
import {EntityObjectArray} from "../domain/entity-object-array";
import {IssueRepository} from "../domain/repositories/issue.repository";

/**
 * The Project properties toEntity() copies from the DTO. A Pick, not Partial<Project>: Partial
 * also carries Entity's methods, and the valueOf() of a plain object literal does not match
 * Entity's valueOf().
 */
type ProjectDtoValues = Pick<Project, 'title' | 'description' | 'shortcut' | 'url' | 'color' | 'employees' | 'creator' | 'deletedAt' | 'milestones'>;

@injectable()
@registry([{
    token: 'transformer.dto_project',
    useToken: DtoToProjectTransformer
}])
@transformer({
    source: `dto`,
    target: Project
})
export class DtoToProjectTransformer extends AbstractDtoToEntityTransformer<ProjectDTO, Project> {

    constructor(
        @inject(ProjectTypeRepository) private readonly projectTypeRepository: ProjectTypeRepository,
        @inject(EmployeeRepository) private readonly employeeRepository: EmployeeRepository
    ) {
        super();
    }

    /**
     * Transform DTO to Project entity.
     * All repository lookups are parallelized to enable request batching.
     *
     * A creator or employee that cannot be found is dropped with a warning instead of failing the
     * transform: the repository rejects an unknown uid, and one rejection inside Promise.all would
     * otherwise reject every project list the project is part of.
     */
    public async toEntity(dto: ProjectDTO): Promise<Project> {

        // Parallel fetch type, creator, and all employees
        const employeePromises = (dto.employees ?? []).map(employeeDTO =>
            (typeof employeeDTO === 'string')
                ? this.employeeRepository.findById([employeeDTO]).then(arr => arr[0])
                    .catch(reason => this.missingEmployee(dto, `employee`, employeeDTO, reason))
                : TransformerHelper.fromDTO<Employee>(employeeDTO, Employee)
        );

        const [type, creator, ...employees] = await Promise.all([
            dto.type ? this.projectTypeRepository.findOneByIdOrDto(dto.type) : Promise.resolve(null),
            dto.creator
                ? this.employeeRepository.findOneByIdOrDto(dto.creator)
                    .catch(reason => this.missingEmployee(dto, `creator`, dto.creator, reason))
                : Promise.resolve(null),
            ...employeePromises
        ]);

        if (!type) {
            console.warn(`Project "${dto.title}" has no type, skipping`);
            return null as unknown as Project;
        }

        const project = this.entityManager.getLoadedEntity(dto.uid) as Project ?? new Project(type);
        const issueRepository = container.resolve(IssueRepository);

        Object.assign<Project, ProjectDtoValues>(project, {
            title: dto.title ?? ``,
            description: dto.description ?? ``,
            shortcut: dto.shortcut ?? ``,
            url: dto.url ?? ``,
            color: dto.color ?? `#FFFFFF`,
            employees: new EntityObjectArray(0, employees.filter(e => e != null)),
            creator,
            deletedAt: !!dto.deletedAt ? DateTime.fromSQL(dto.deletedAt) : null,
            milestones: issueRepository.findById(dto.milestones),
        });

        // to set as new valid unchanged state
        project.commit();

        return project;
    }

    /**
     * Logs a reference to an employee that cannot be resolved and stands in null for it.
     */
    private missingEmployee(dto: ProjectDTO, role: string, reference: unknown, reason: unknown): null {
        const uid = typeof reference === `string` ? reference : (reference as { uid?: string } | null)?.uid;
        console.warn(`Project "${dto.title}" (${dto.uid}): ${role} ${uid} cannot be resolved and is left out`, reason);
        return null;
    }

}